// Live-camera helpers (getUserMedia) + canvas -> JPEG capture. Used by the
// Upload pane and the "Take a better picture" recapture. We always export a
// JPEG from a canvas — never the raw camera frame / HEIC. CONTRACTS §10, DESIGN §9.

import { useCallback, useEffect, useRef, useState } from "react";

export type CameraStatus = "idle" | "starting" | "ready" | "error";

export interface UseCameraResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: CameraStatus;
  error: string | null;
  /** Start (or restart) the stream. Safe to call repeatedly. */
  start: () => Promise<void>;
  /** Stop all tracks and detach. */
  stop: () => void;
  /** True if the browser exposes a camera API at all. */
  supported: boolean;
}

const cameraSupported =
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function";

/**
 * Manage a rear-facing live camera bound to a <video>. Requests the highest
 * resolution the device offers (good for the full-res "take a better picture")
 * and the environment (rear) camera, which is what a docent scanning a binder
 * wants.
 */
export function useCamera(active: boolean): UseCameraResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setStatus("idle");
  }, []);

  const start = useCallback(async () => {
    if (!cameraSupported) {
      setStatus("error");
      setError(
        "This browser has no camera API. Open the site over HTTPS (or localhost) in Safari/Chrome.",
      );
      return;
    }
    setStatus("starting");
    setError(null);
    try {
      // Stop any prior stream before starting a new one.
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) t.stop();
        streamRef.current = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 4096 },
          height: { ideal: 4096 },
        },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        // iOS Safari requires these for inline autoplay.
        video.setAttribute("playsinline", "true");
        video.muted = true;
        try {
          await video.play();
        } catch {
          // Autoplay can reject; the stream is still attached and will render.
        }
      }
      setStatus("ready");
    } catch (err) {
      setStatus("error");
      const name = err instanceof DOMException ? err.name : "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError(
          "Camera permission was denied. Allow camera access for this site and try again.",
        );
      } else if (name === "NotFoundError") {
        setError("No camera was found on this device.");
      } else {
        setError(err instanceof Error ? err.message : "Could not start the camera.");
      }
    }
  }, []);

  // Drive start/stop from the `active` flag (e.g. only when the Upload pane is shown).
  useEffect(() => {
    if (active) {
      void start();
    } else {
      stop();
    }
    return () => stop();
  }, [active, start, stop]);

  return { videoRef, status, error, start, stop, supported: cameraSupported };
}

/**
 * Grab the current video frame into a canvas and export a JPEG Blob.
 * Returns null if the video has no dimensions yet or encoding fails.
 */
export function captureJpegFromVideo(
  video: HTMLVideoElement,
  quality = 0.92,
): Promise<Blob | null> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return Promise.resolve(null);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(video, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

/** A crop rectangle in the source image's NATURAL pixel coordinates. */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Crop a JPEG Blob to `rect` (natural pixels) and re-encode as JPEG. Used before
 * upload to tighten the frame to just the artwork — better reverse-image lookups
 * and a cleaner fallback when no online match is found (DESIGN §7). Output stays
 * image/jpeg so the presigned PUT's Content-Type contract is unchanged (api.ts).
 * Returns the original blob unchanged if anything goes wrong (never throws).
 */
export async function cropJpeg(
  source: Blob,
  rect: CropRect,
  quality = 0.92,
): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(source);
  } catch {
    return source;
  }
  try {
    const sx = Math.max(0, Math.round(rect.x));
    const sy = Math.max(0, Math.round(rect.y));
    const sw = Math.min(bitmap.width - sx, Math.max(1, Math.round(rect.w)));
    const sh = Math.min(bitmap.height - sy, Math.max(1, Math.round(rect.h)));
    if (sw <= 0 || sh <= 0) return source;
    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");
    if (!ctx) return source;
    ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, sw, sh);
    return await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob ?? source), "image/jpeg", quality);
    });
  } finally {
    bitmap.close?.();
  }
}

/**
 * Re-encode a JPEG Blob rotated clockwise by `degrees` (any multiple of 90; other
 * values are normalised to the nearest 0/90/180/270). At 90/270 the canvas width
 * and height are swapped. We rotate the BITMAP (not the crop rect), so the
 * cropper can simply replace its working source with the rotated result and the
 * existing cropJpeg rect math (in the rotated image's natural pixels) applies
 * unchanged. Output stays image/jpeg so the presigned PUT's Content-Type contract
 * is unchanged (api.ts). Returns the original blob unchanged if anything goes
 * wrong, or when there is no rotation to apply (never throws).
 */
export async function rotateJpeg(
  source: Blob,
  degrees: number,
  quality = 0.92,
): Promise<Blob> {
  // Normalise to one of 0/90/180/270 (clockwise).
  const turn = (((Math.round(degrees / 90) % 4) + 4) % 4) * 90;
  if (turn === 0) return source;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(source);
  } catch {
    return source;
  }
  try {
    const bw = bitmap.width;
    const bh = bitmap.height;
    const swap = turn === 90 || turn === 270;
    const canvas = document.createElement("canvas");
    canvas.width = swap ? bh : bw;
    canvas.height = swap ? bw : bh;
    const ctx = canvas.getContext("2d");
    if (!ctx) return source;
    // Move origin to the canvas centre, rotate, then draw the image centred so it
    // maps onto the (possibly swapped) canvas without clipping.
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((turn * Math.PI) / 180);
    ctx.drawImage(bitmap, -bw / 2, -bh / 2);
    return await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob ?? source), "image/jpeg", quality);
    });
  } finally {
    bitmap.close?.();
  }
}
